import { Box, Card, Icon, Paragraph, TextButton } from '@zillow/constellation-10';
import { IconCheckmarkFilled } from '@zillow/constellation-icons';
import type { SatelliteWebLandlordLeadManagementTableRowResponseDto } from '@zg-rentals/ts-satellite-client';
import { forwardRef, memo, useContext } from 'react';
import { AppContext } from '../../../contexts/AppContextProvider';
import { LeadManagementContext } from '../../../contexts/LeadManagementContextProvider';
import ScreeningCriteriaMatches from '../../ScreeningCriteriaMatches';
import { getFormattedAttributesList } from '../../../utils/renterProfileUtils';
import { ModalType } from '../../../../src/types/ModalType';
import useModal from '../../../hooks/useModal';
import { leadManagementShowProfileRequestedForConversationIdStore } from '../../../lib/leadManagementStores';

type RenterProfileAttributeProps = {
  title: string;
  content: string;
};

export type RenterDetailsScreeningCriteriaProps = {
  lead: SatelliteWebLandlordLeadManagementTableRowResponseDto | null;
  conversationId: string;
  isBackgroundHighlighted: boolean;
};

const RenterProfileAttribute = memo(({ title = '', content = '' }: RenterProfileAttributeProps) => {
  return (
    <Box css={{ display: 'flex', flex: '0 0 50%', marginTop: 'tighter' }}>
      <Paragraph textStyle="body-sm">{title}:</Paragraph>
      <Paragraph textStyle="body-sm" css={{ color: 'text.subtle', marginLeft: 'tighter' }}>
        {content}
      </Paragraph>
    </Box>
  );
});

const RenterDetailsScreeningCriteria = forwardRef<HTMLParagraphElement, RenterDetailsScreeningCriteriaProps>(
  ({ lead, conversationId, isBackgroundHighlighted }, ref): JSX.Element => {
    const { isImpersonator } = useContext(AppContext);
    const { isArchivedFolder, isReportedFolder } = useContext(LeadManagementContext);
    const { setActiveModal } = useModal();
    const leadManagementShowProfileRequestedForConversationId =
      leadManagementShowProfileRequestedForConversationIdStore.useValue();

    const { screeningCriteria, latestContact, renterProfile } = lead || {};
    const {
      criteria,
      numRequiredOrNegotiableCriteriaSelectedByLandlordThatMatch,
      numCriteriaSelectedByLandlord,
      criteriaMatchedDisplay,
      shouldShowRequestRenterProfileCta,
    } = screeningCriteria || {};
    const { isPhoneLead } = latestContact || {};
    const { attributes } = renterProfile || {};
    const attributesList = getFormattedAttributesList(attributes);
    const hasMatchedAllCriteria =
      numRequiredOrNegotiableCriteriaSelectedByLandlordThatMatch !== 0 &&
      numCriteriaSelectedByLandlord !== 0 &&
      numRequiredOrNegotiableCriteriaSelectedByLandlordThatMatch === numCriteriaSelectedByLandlord;
    const shouldShowProfileRequested = leadManagementShowProfileRequestedForConversationId === conversationId;

    const handleShowRequestRenterProfileModal = () => setActiveModal(ModalType.RequestRenterProfileModal);

    return (
      <>
        <Paragraph textStyle="body-sm-bold" ref={ref} css={{ marginTop: 'loose' }}>
          Renter Profile
        </Paragraph>
        <Card
          elevated={false}
          css={{
            marginTop: 'default',
            padding: 'loose',
            backgroundColor: isBackgroundHighlighted ? 'blue.100' : 'white',
          }}
        >
          {shouldShowRequestRenterProfileCta || shouldShowProfileRequested ? (
            <>
              <Paragraph textStyle="body-sm">No details provided</Paragraph>
              {shouldShowProfileRequested ? (
                <Box css={{ display: 'flex', alignItems: 'center', marginTop: 'default' }}>
                  <Paragraph textStyle="body-sm" css={{ color: 'coolGray.500' }}>
                    Profile requested
                  </Paragraph>
                  <Icon css={{ color: 'icon.subtle', marginLeft: 'tighter' }}>
                    <IconCheckmarkFilled />
                  </Icon>
                </Box>
              ) : (
                <TextButton
                  textStyle="body-sm-bold"
                  disabled={isArchivedFolder || isReportedFolder || isImpersonator}
                  onClick={handleShowRequestRenterProfileModal}
                  css={{ marginTop: 'default' }}
                >
                  Request a profile
                </TextButton>
              )}
            </>
          ) : (
            <>
              {isPhoneLead || criteria === undefined || criteria.length === 0 ? (
                <Paragraph textStyle="body-sm-bold">Screening criteria: Not available</Paragraph>
              ) : (
                <Paragraph
                  textStyle="body-sm-bold"
                  css={{ display: 'flex', alignItems: 'center', marginBottom: 'tighter' }}
                >
                  Screening criteria:&nbsp;
                  {criteriaMatchedDisplay}
                  {hasMatchedAllCriteria && (
                    <Icon css={{ color: 'green.600', marginLeft: 'tighter' }} size="sm">
                      <IconCheckmarkFilled />
                    </Icon>
                  )}
                </Paragraph>
              )}

              <ScreeningCriteriaMatches criteria={criteria || []} isRenterProfileStyle={true} />
              <Paragraph textStyle="body-sm-bold" css={{ marginTop: 'loose' }}>
                Additional details{attributesList.length === 0 ? ': Not available' : ''}
              </Paragraph>

              <Box css={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'column', lg: { flexDirection: 'row' } }}>
                {attributesList.map((attribute) => (
                  <RenterProfileAttribute key={attribute.title} title={attribute.title} content={attribute.value} />
                ))}
              </Box>
              <Paragraph textStyle="body-xs" css={{ color: 'text.subtle', marginTop: 'loose' }}>
                This information was self-reported by the renter
              </Paragraph>
            </>
          )}
        </Card>
      </>
    );
  },
);

RenterProfileAttribute.displayName = 'RenterProfileAttribute';
RenterDetailsScreeningCriteria.displayName = 'RenterDetailsScreeningCriteria';
export default RenterDetailsScreeningCriteria;
